// https://cydstumpel.nl/

import * as THREE from 'three'
import { useRef, useState, useEffect } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useTexture, Environment, ScrollControls, useScroll } from '@react-three/drei'
import { easing } from 'maath'
import './util'
import { useStore } from './index'

const achievements = [
  { imageName: 'Attendance.jpg', score: 15 },
  { imageName: 'OneToOnes.jpg', score: 10 },
  { imageName: 'CEUs.jpg', score: 15 },
  { imageName: 'Testimonial.jpg', score: 5 },
  { imageName: 'TYFTB.jpg', score: 5 },
  { imageName: 'Visitors.jpg', score: 5 },
  { imageName: 'Referrals.jpg', score: 5 },
]

export const App = () => {
  const showBanner = useStore((state) => state.showBanner)
  
  return (
    <Canvas camera={{ position: [0, 0, 100], fov: 15 }}>
      <fog attach="fog" args={['#a79', 8.5, 12]} />
      <ScrollControls pages={4} infinite>
        <Rig rotation={[0, 0, 0.15]}>
          <Carousel />
        </Rig>
        {showBanner && <Banner position={[0, -0.15, 0]} />}
      </ScrollControls>
      <Environment preset="dawn" background blur={0.5} />
    </Canvas>
  )
}

function Rig(props) {
  const ref = useRef()
  const scroll = useScroll()
  useFrame((state, delta) => {
    ref.current.rotation.y = -scroll.offset * (Math.PI * 2) // Rotate contents
    state.events.update() // Raycasts every frame rather than on pointer-move
    easing.damp3(state.camera.position, [-state.pointer.x * 2, state.pointer.y + 1.5, 10], 0.3, delta) // Move camera
    state.camera.lookAt(0, 0, 0) // Look at center
  })
  return <group ref={ref} {...props} />
}

function Carousel() {
  return achievements.map((achievement, i) => (
    <Card
      key={i}
      imageName={achievement.imageName}
      score={achievement.score}
      url={`/${achievement.imageName}`}
      position={[Math.sin((i / achievements.length) * Math.PI * 2) * 1.4, 0, Math.cos((i / achievements.length) * Math.PI * 2) * 1.4]}
      rotation={[0, (i / achievements.length) * Math.PI * 2, 0]}
    />
  ))
}

function Card({ imageName, score, url, ...props }) {
  const ref = useRef()
  const materialRef = useRef()
  const [hovered, setHovered] = useState(false)
  const [colored, setColored] = useState(false)
  const texture = useTexture(url)
  const incrementScore = useStore((state) => state.incrementScore)

  // Make sure texture is properly loaded
  useEffect(() => {
    if (texture) {
      texture.needsUpdate = true
    }
  }, [texture])

  const handlePointerOver = (e) => {
    e.stopPropagation()
    setHovered(true)
  }

  const handlePointerOut = () => {
    setHovered(false)
  }

  const handleClick = (e) => {
    e.stopPropagation()
    setColored(true)
    incrementScore(imageName, score)
  }

  useFrame((state, delta) => {
    if (ref.current) {
      // Scale animation
      easing.damp3(ref.current.scale, hovered ? 1.15 : 1, 0.1, delta)
    }

    if (materialRef.current) {
      // Color transition animation with easing
      easing.damp(materialRef.current.uniforms.colorFactor, 'value', colored ? 1.0 : 0.0, 0.25, delta)
    }
  })

  return (
    <mesh ref={ref} onPointerOver={handlePointerOver} onPointerOut={handlePointerOut} onClick={handleClick} {...props}>
      <bentPlaneGeometry args={[0.1, 1, 1, 20, 20]} />
      <grayscaleTransitionMaterial ref={materialRef} map={texture} transparent side={THREE.DoubleSide} />
    </mesh>
  )
}

function Banner(props) {
  const ref = useRef()
  const materialRef = useRef()
  const texture = useTexture('/pala.png')
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping
  const scroll = useScroll()
  
  // Set up the material
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.transparent = true
    }
  }, [])
  
  useFrame((state, delta) => {
    if (ref.current && materialRef.current) {
      ref.current.material.time.value += Math.abs(scroll.delta) * 4
      ref.current.material.map.offset.x += delta / 2
    }
  })
  
  return (
    <mesh ref={ref} {...props}>
      <cylinderGeometry args={[1.6, 1.6, 0.14, 128, 16, true]} />
      <meshSineMaterial 
        ref={materialRef}
        map={texture} 
        map-anisotropy={16} 
        map-repeat={[30, 1]} 
        side={THREE.DoubleSide} 
        toneMapped={false}
        transparent
        opacity={1}
      />
    </mesh>
  )
}
